import classnames from 'classnames';
import React, { useEffect } from 'react';
import { Badge, Container, Spinner } from 'react-bootstrap';
import { useHistory, useLocation } from 'react-router';
import { connect, useSelector } from 'react-redux';
import {
  NavLink,
  Redirect,
  Route,
  RouteComponentProps,
  Switch,
  withRouter,
} from 'react-router-dom';
import Count from 'v2/common/Count/Count';
import {
  GenericObject,
  SearchEntity,
  User,
  DjangoListResponse,
  SearchFilterType,
  SearchQuestionEntity,
  Permission,
} from 'v2/models';
import { mapNoteSeverity } from 'v2/utils/noteUtils';
import { archiveSearch, updateSearch } from '../../../actions';
import NomadAlert from '../../common/NomadAlert/NomadAlert';
import angleIcon from '../../icons/angle.svg';
import commentDotsIcon from '../../icons/commentDots.svg';
import envelopeIcon from '../../icons/envelope.svg';
import listIcon from '../../icons/list.svg';
import SearchDetail from './SearchDetail';
import styles from './searchDetailsContainer.module.scss';
import { useQuery } from 'react-query';
import restFactory from 'v2/utils/restFactory';
import { REVIEW_STATUS } from 'v2/utils/searchUtils';
import ChatSelector from 'v2/Chat/ChatSelector';
import { getKeyCount, filterApprovedProposals } from 'v2/utils/proposalUtils';
import { searchViewPath } from 'v2/utils/searchViewUtils';
import { isRequestViewerOnly } from 'v2/utils/user';
import { SearchResponsesSwitch } from './SearchResponse/SearchResponsesSwitch';
import { useFreeUser } from 'v2/hooks/useFreeUser';
import { useArtificialResponses } from 'v2/queries/useArtificialResponses';
import useSelectConversationCount from 'v2/hooks/useSelectConversationCount';
import { useSearchConversationCount } from 'v2/queries/useSearchConversationCount';
import { useSearchDetails } from 'v2/queries/searches/useSearchDetails';
import { ExpiredTooltip } from 'v2/common/SearchList/SearchItem/Columns/ExpiredTooltip/ExpiredTooltip';

interface DispatchProps {
  // eslint-disable-next-line @typescript-eslint/ban-types
  archiveSearch: Function;
  // eslint-disable-next-line @typescript-eslint/ban-types
  updateSearch: Function;
}
interface StateProps {
  user: User;
  permissions: {
    permissions: { [key: string]: Permission };
    loaded: boolean;
  };
  unreadResponsesCount: number;
  conversationsCount: number;
}

interface locationStateType {
  counterToGoBack: number;
  comingFromPath?: string;
}

export type SearchRouterProps = RouteComponentProps<{ id: string }>;

type SearchDetailsProps = DispatchProps & StateProps & SearchRouterProps;

export const SearchDetailsContainer = ({
  user,
  permissions,
  unreadResponsesCount,
  match,
  archiveSearch,
  updateSearch,
}: SearchDetailsProps) => {
  const { path, url, params } = match;
  const searchId = Number(params.id);
  const history = useHistory();
  const location = useLocation();
  const comingFromPath = useLocation<locationStateType>().state?.comingFromPath;
  const [isFreeUser] = useFreeUser();
  const counterToGoBackFromState =
    useLocation<locationStateType>().state?.counterToGoBack;
  const counterToGoBack = counterToGoBackFromState
    ? counterToGoBackFromState
    : -1;
  const { data } = useSearchConversationCount(searchId);
  const { conversations_count = 0 } = data || {};
  const hasConversations = conversations_count > 0;
  const selectApprovedProposals = useSelector((state: GenericObject) => {
    const approvedProposals = filterApprovedProposals(
      state.proposals.multipleProposals
    );
    return getKeyCount(approvedProposals);
  });

  const isAllSearchesTab: boolean = comingFromPath
    ? comingFromPath.includes(searchViewPath.allSearches)
    : false;

  const selectConversationCount =
    useSelectConversationCount(isAllSearchesTab)?.[searchId];

  const { data: searchDetails, isSuccess: isSearchDetailsSuccess } =
    useSearchDetails(searchId);

  const { data: searchFilters, isSuccess: isSearchFiltersSuccess } = useQuery(
    ['Get filters related to request by ID', searchId],
    async () => {
      const response = await restFactory.get<
        DjangoListResponse<SearchFilterType>
      >(`/dj/api/v1/search/${searchId}/filters/`, { size: 100 });

      return response.results;
    },
    { cacheTime: 0 }
  );

  const { data: searchQuestions, isSuccess: isSearchQuestionsSuccess } =
    useQuery(
      ['Get questions related to request by ID', searchId],
      async () => {
        const response = await restFactory.get<
          DjangoListResponse<SearchQuestionEntity>
        >(`/dj/api/v1/search/${searchId}/questions/`, { size: 100 });
        return response.results;
      },
      { cacheTime: 0 }
    );

  const { isLoading: searchConversationsCountLoading } =
    useSearchConversationCount(searchId);

  const { data: freeResponses = { count: 0 } } = useArtificialResponses({
    search: searchDetails,
    enabled: isFreeUser,
  });

  const allSearchesBadgeStyle = () =>
    location.pathname.includes('/reviewer/') &&
    searchDetails?.review_status === 'approved';

  const getResponsesCount = () => {
    if (isFreeUser) {
      return freeResponses.count;
    }
    return allSearchesBadgeStyle()
      ? selectApprovedProposals[Number(params.id)]
      : unreadResponsesCount;
  };

  const LINKS = [
    {
      to: `${url}/detail`,
      label: 'Request Details',
      icon: listIcon,
    },
    {
      to: `${url}/responses`,
      label: 'Responses',
      icon: envelopeIcon,
      count: getResponsesCount(),
    },
    {
      to: `${url}/conversations`,
      label: 'Conversations',
      icon: commentDotsIcon,
      count: selectConversationCount,
    },
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const queriesSucceded = [
    isSearchDetailsSuccess,
    isSearchFiltersSuccess,
    isSearchQuestionsSuccess,
  ].every((s) => s);

  const missingData = [searchDetails, searchFilters, searchQuestions].some(
    (s) => !s
  );

  if (!queriesSucceded) {
    return null;
  }

  if (missingData) {
    return <Redirect to="/" />;
  }

  const search = {
    ...searchDetails,
    questions: searchQuestions,
    filters: searchFilters,
  } as SearchEntity;

  if (searchConversationsCountLoading) {
    return (
      <div className={classnames(styles.loading)} aria-label="loading-spinner">
        <Spinner role="progressbar" animation="border" />
      </div>
    );
  }

  return (
    <>
      <div
        className={classnames('bg-white h-100', styles.searchDetailsContainer)}
      >
        {search.note && (
          <div className="bg-light">
            <Container
              className={classnames(
                'fix-bg-images pt-4',
                styles.alertContainer
              )}
              fluid="xl"
            >
              <NomadAlert
                className="d-flex py-3"
                severity={mapNoteSeverity(search.note.severity)}
                title={search.note.title}
                content={search.note.content}
                icon={search.note.icon}
              ></NomadAlert>
            </Container>
          </div>
        )}
        <div
          className={classnames(
            'd-flex',
            'justify-content-between',
            'bg-light',
            'position-relative',
            'z-index-1',
            'page-title'
          )}
        >
          <div className="container-xl d-flex justify-content-between position-relative fix-bg-images">
            <div className="d-flex align-items-center text-truncate">
              <div
                onClick={() => {
                  if (history.length > 2) {
                    history.go(counterToGoBack);
                  } else {
                    history.replace('/requests');
                  }
                }}
                className={classnames('text-truncate', styles.header)}
              >
                <div className={classnames(styles.prevLink)}>
                  <img src={angleIcon} alt="angleIcon"></img>
                  <h1> My Requests </h1>
                </div>
                <div className={classnames(styles.searchTitle)}>
                  <ExpiredTooltip {...search} detail />
                  <h1 className="m-0 text-truncate pr-1">{search.name}</h1>
                </div>
              </div>
            </div>
            <div
              className={classnames(styles.pills, 'd-flex align-items-center')}
            >
              {LINKS.map((link, index) => {
                if (
                  link.label === 'Conversations' &&
                  (!hasConversations || isFreeUser)
                )
                  return null;

                if (
                  index === 1 &&
                  search.review_status !== REVIEW_STATUS.APPROVED
                ) {
                  return null;
                }
                if (
                  index === 2 &&
                  isRequestViewerOnly(permissions.permissions)
                ) {
                  return null;
                }
                return (
                  <NavLink
                    key={index}
                    to={{
                      pathname: link.to,
                      state: {
                        counterToGoBack: counterToGoBack - 1,
                        comingFromPath,
                      },
                    }}
                    className="subNavLink"
                    data-cy="request-navbar-link"
                  >
                    <Badge
                      pill
                      className="mr-2 mr-lg-5 d-flex align-items-center"
                    >
                      <img src={link.icon} className="mr-2" alt="navIcon"></img>
                      {link.label}
                      {link.count ? (
                        <Count
                          className={
                            allSearchesBadgeStyle()
                              ? styles.ReviewerSearchDetailCount
                              : 'ml-2'
                          }
                          value={link.count}
                        />
                      ) : null}
                    </Badge>
                  </NavLink>
                );
              })}
            </div>
          </div>
        </div>
        <div
          className={classnames(
            'bg-white h-100 px-4',
            styles.routeDetailContainer
          )}
        >
          <Switch>
            <Route path={`${path}/detail`}>
              <SearchDetail
                user={user}
                search={search}
                hasUnreadResponses={!!unreadResponsesCount}
                archiveSearch={
                  archiveSearch as unknown as (
                    searchId: number
                  ) => Promise<unknown>
                }
                updateSearch={
                  updateSearch as unknown as (
                    searchDetails: Partial<SearchEntity>
                  ) => Promise<unknown>
                }
                counterToGoBack={counterToGoBack}
                comingFromPath={comingFromPath}
              />
            </Route>
            <Route path={`${path}/responses`}>
              <SearchResponsesSwitch search={search} />
            </Route>
            <Route path={`${path}/conversations`}>
              <ChatSelector search={search} comingFromPath={comingFromPath} />
            </Route>
          </Switch>
        </div>
      </div>
    </>
  );
};

const mapStateToProps = (state: GenericObject, ownProps: SearchRouterProps) => {
  const searchId = Number(ownProps.match.params.id);
  return {
    user: state.user.user,
    permissions: state.permissions,
    unreadResponsesCount:
      state.proposals?.unreadProposalsCount?.[`${searchId}`],
    conversationsCount: state.chat?.unreadMessages?.[`${searchId}`],
  };
};

export default connect<StateProps, DispatchProps, SearchRouterProps>(
  mapStateToProps,
  {
    archiveSearch,
    updateSearch,
  }
)(withRouter(SearchDetailsContainer));
